import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Typography,
} from "@material-ui/core";
import { Facebook, Instagram } from "@material-ui/icons";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const POLL_INTERVAL_MS = 1500;

const providerIcon = (type) =>
  type === "facebook" ? (
    <Facebook style={{ color: "#1877f2" }} />
  ) : (
    <Instagram style={{ color: "#e1306c" }} />
  );

const MetaConnectionModal = ({
  open,
  onClose,
  onConnected,
  initialChannel,
  reconnectConnectionId,
  permittedChannels = ["facebook", "instagram"],
}) => {
  const [assets, setAssets] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [correlationId, setCorrelationId] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const popupRef = useRef(null);

  const backendOrigin = useMemo(() => {
    try {
      return new URL(process.env.REACT_APP_BACKEND_URL).origin;
    } catch (_error) {
      return "";
    }
  }, []);

  const selectableAssets = useMemo(
    () =>
      assets.filter(
        (asset) =>
          permittedChannels.includes(asset.type) && asset.status !== "revoked",
      ),
    [assets, permittedChannels],
  );

  const loadAssets = useCallback(async () => {
    const { data } = await api.get("/meta/assets");
    setAssets(Array.isArray(data) ? data : []);
    setSelectedIds(
      (Array.isArray(data) ? data : [])
        .filter((asset) =>
          initialChannel ? asset.type === initialChannel : !asset.selected,
        )
        .filter((asset) => !asset.selected && asset.status !== "revoked")
        .map((asset) => asset.id),
    );
  }, [initialChannel]);

  useEffect(() => {
    if (!open) return;

    setStatus("loading");
    setError("");
    loadAssets()
      .then(() => setStatus("idle"))
      .catch((requestError) => {
        setStatus("idle");
        toastError(requestError);
      });
  }, [loadAssets, open]);

  const checkOAuthResult = useCallback(async () => {
    if (!correlationId) return;

    try {
      const { data } = await api.get(`/meta/onboarding/${correlationId}`);
      if (data.status === "pending") return;

      if (data.status === "succeeded") {
        await loadAssets();
        setStatus("selecting");
        setError("");
        return;
      }

      setStatus("idle");
      setError(
        data.status === "cancelled"
          ? "A autorizacao foi cancelada. Voce pode tentar novamente."
          : "A Meta nao concluiu a autorizacao. Revise as permissoes e tente novamente.",
      );
    } catch (requestError) {
      setStatus("idle");
      toastError(requestError);
    }
  }, [correlationId, loadAssets]);

  useEffect(() => {
    if (status !== "authorizing" || !correlationId) return undefined;
    const timer = window.setInterval(checkOAuthResult, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [checkOAuthResult, correlationId, status]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin !== backendOrigin ||
        event.data?.type !== "fluyra:meta-oauth" ||
        event.data?.correlationId !== correlationId
      ) {
        return;
      }
      checkOAuthResult();
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [backendOrigin, checkOAuthResult, correlationId]);

  useEffect(
    () => () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    },
    [],
  );

  const startAuthorization = async () => {
    setError("");
    const popup = window.open(
      "about:blank",
      "fluyra-meta-oauth",
      "popup=yes,width=620,height=760",
    );
    if (!popup) {
      setError("Permita pop-ups neste site para continuar com a Meta.");
      return;
    }

    popupRef.current = popup;
    setStatus("authorizing");
    try {
      const endpoint = reconnectConnectionId
        ? `/meta/connections/${reconnectConnectionId}/reconnect`
        : "/meta/oauth/start";
      const { data } = await api.post(endpoint);
      setCorrelationId(data.correlationId);
      popup.location.assign(data.authorizationUrl);
    } catch (requestError) {
      popup.close();
      setStatus("idle");
      toastError(requestError);
    }
  };

  const toggleAsset = (assetId) => {
    setSelectedIds((current) =>
      current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId],
    );
  };

  const connectAssets = async () => {
    if (selectedIds.length === 0) {
      setError("Selecione ao menos uma Pagina ou conta do Instagram.");
      return;
    }

    setStatus("saving");
    setError("");
    try {
      const { data } = await api.post("/meta/connections", {
        assetIds: selectedIds,
      });
      if (onConnected) onConnected(data.connections || []);
      onClose();
    } catch (requestError) {
      setStatus("selecting");
      toastError(requestError);
    }
  };

  const handleClose = () => {
    if (status === "saving") return;
    setCorrelationId("");
    setStatus("idle");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Conectar Facebook e Instagram</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          Entre pela Meta, autorize as Paginas da sua empresa e escolha quais
          canais devem receber mensagens no Fluyra. Nenhuma senha ou token fica
          exposto no navegador.
        </Typography>

        {error && (
          <Box mt={2}>
            <Typography color="error" role="alert">
              {error}
            </Typography>
          </Box>
        )}

        {(status === "loading" || status === "authorizing") && (
          <Box
            py={4}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <CircularProgress size={28} />
            <Box ml={2}>
              <Typography>
                {status === "authorizing"
                  ? "Aguardando a autorizacao na Meta..."
                  : "Carregando ativos autorizados..."}
              </Typography>
            </Box>
          </Box>
        )}

        {status !== "loading" && status !== "authorizing" && (
          <Box mt={2} display="flex" flexDirection="column" gridGap={12}>
            {selectableAssets.length === 0 ? (
              <Paper variant="outlined">
                <Box p={2}>
                  <Typography variant="body2">
                    Nenhum ativo Meta autorizado para esta empresa.
                  </Typography>
                </Box>
              </Paper>
            ) : (
              selectableAssets.map((asset) => (
                <Paper variant="outlined" key={asset.id}>
                  <Box
                    p={1.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          color="primary"
                          checked={
                            asset.selected || selectedIds.includes(asset.id)
                          }
                          disabled={asset.selected}
                          onChange={() => toggleAsset(asset.id)}
                        />
                      }
                      label={
                        <Box display="flex" alignItems="center" gridGap={8}>
                          {providerIcon(asset.type)}
                          <span>
                            {asset.username ? `@${asset.username}` : asset.name}
                          </span>
                        </Box>
                      }
                    />
                    <Chip
                      size="small"
                      color={asset.selected ? "primary" : "default"}
                      label={asset.selected ? "Conectado" : "Disponivel"}
                    />
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={status === "saving"}>
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="outlined"
          onClick={startAuthorization}
          disabled={
            status === "loading" ||
            status === "authorizing" ||
            status === "saving"
          }
        >
          Autorizar pela Meta
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={connectAssets}
          disabled={
            status === "loading" ||
            status === "authorizing" ||
            status === "saving"
          }
        >
          {status === "saving" ? "Conectando..." : "Conectar selecionados"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MetaConnectionModal;
